import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  AlertCircle,
  BookOpen,
  CalendarDays,
  Clock,
  HandHeart,
  MapPin,
  RefreshCw,
  Users,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Banner,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  cn,
  Container,
  Dialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Hero,
  HERO_PARALLAX,
  Input,
  Label,
  LoadingDots,
  PageHeader,
  PillarCard,
  Section,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  WorldMapMotif,
  CardCarousel,
  Reveal,
  RevealGroup,
} from "@alight/ui";
import { Logo, LogoMark } from "./Logo";

/* ═══════════════════════════════════════════════════════════════════════════
 * Reading the theme LIVE
 *
 * Nothing on this page keeps its own list of colours. Every token is read from
 * the document as it is right now, so a colour a designer adds to theme.css —
 * or a whole palette the Studio's theme try-on drops onto <html> — appears
 * here on its own with no edit to this file.
 * ═════════════════════════════════════════════════════════════════════════ */

type Token = { name: string; value: string };
type ThemeSnapshot = { colours: Token[]; values: Record<string, string> };

const EMPTY_SNAPSHOT: ThemeSnapshot = { colours: [], values: {} };

/** Pull every `--custom-property` name declared by a set of CSS rules. */
function collectFromRules(rules: CSSRuleList, into: Set<string>) {
  for (const rule of Array.from(rules)) {
    if (rule instanceof CSSStyleRule) {
      // Only the document-wide block — that is where theme tokens live.
      if (!rule.selectorText.includes(":root")) continue;
      for (let i = 0; i < rule.style.length; i += 1) {
        const property = rule.style.item(i);
        if (property.startsWith("--")) into.add(property);
      }
    } else if ("cssRules" in rule) {
      // @media / @supports / @layer wrappers — look inside them too.
      try {
        collectFromRules((rule as CSSGroupingRule).cssRules, into);
      } catch {
        /* ignore an unreadable group */
      }
    }
  }
}

/** Every theme token name the document knows about, from both sources. */
function collectTokenNames(): Set<string> {
  const names = new Set<string>();

  // 1. Every stylesheet on the page. Reading `cssRules` on a stylesheet from
  //    another domain throws a security error, so each one is guarded.
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      collectFromRules(sheet.cssRules, names);
    } catch {
      continue;
    }
  }

  // 2. Properties set straight onto <html>. This is how the Studio's theme
  //    try-on paints a candidate look over the top of theme.css, so a tried-on
  //    palette shows up here as well.
  const inline = document.documentElement.style;
  for (let i = 0; i < inline.length; i += 1) {
    const property = inline.item(i);
    if (property.startsWith("--")) names.add(property);
  }

  return names;
}

function readTheme(): ThemeSnapshot {
  const computed = getComputedStyle(document.documentElement);
  const values: Record<string, string> = {};
  for (const name of collectTokenNames()) {
    const value = computed.getPropertyValue(name).trim();
    if (value !== "") values[name] = value;
  }
  // BRAND ORDER, not alphabetical. Sorted A–Z, `--color-accent` led the page
  // while the site is primary-led — enough to make a designer think the guide
  // and the site disagree. Lead with the brand colours in the order people
  // talk about them, then supporting tones, then anything new the theme has
  // grown (which still appears automatically, at the end).
  const BRAND_ORDER = [
    "--color-primary",
    "--color-secondary",
    "--color-tertiary",
    "--color-accent",
    "--color-band",
    "--color-ink",
    "--color-paper",
    "--color-card",
    "--color-muted",
    "--color-border",
    "--color-ring",
    "--color-success",
    "--color-info",
    "--color-warning",
    "--color-error",
  ];
  const rank = (name: string) => {
    const base = name.replace(/-fg$/, "");
    const i = BRAND_ORDER.indexOf(base);
    return i === -1 ? BRAND_ORDER.length : i;
  };
  const colours = Object.keys(values)
    .filter((name) => name.startsWith("--color-"))
    .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
    .map((name) => ({ name, value: values[name] ?? "" }));
  return { colours, values };
}

/**
 * Re-reads the theme whenever it changes: trying on a look sets properties on
 * <html>, and editing theme.css in dev swaps a <style> tag in the head.
 */
function useLiveTheme(): ThemeSnapshot {
  const [snapshot, setSnapshot] = React.useState<ThemeSnapshot>(EMPTY_SNAPSHOT);

  React.useEffect(() => {
    let frame = 0;
    const refresh = () => {
      frame = 0;
      setSnapshot(readTheme());
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(refresh);
    };

    refresh();
    const observer = new MutationObserver(schedule);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style"],
    });
    observer.observe(document.head, { childList: true });
    return () => {
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return snapshot;
}

/** Colours are stored as `27 54 93` (red green blue), not as a colour name. */
const CHANNELS = /^\d{1,3}\s+\d{1,3}\s+\d{1,3}$/;

/**
 * A paintable value for a token — always a reference to the variable, never a
 * copied-out colour, so every swatch restyles the instant the theme changes.
 */
function paint(token: Token): string {
  return CHANNELS.test(token.value) ? `rgb(var(${token.name}))` : `var(${token.name})`;
}

/** The first family in a font stack, e.g. `Georgia` from `Georgia, serif`. */
function leadFamily(stack: string): string {
  const first = stack.split(",")[0]?.trim() ?? "";
  return first.replace(/^["']|["']$/g, "") || "not set";
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Small presentational helpers
 * ═════════════════════════════════════════════════════════════════════════ */

/** How see-through a colour can be used, as a percentage of full strength. */
const STRENGTHS = [10, 20, 40, 60, 80, 100] as const;

/**
 * The entrance classes, written out in full. Tailwind only keeps a class it can
 * read literally in the source, so these can never be built up from pieces —
 * the Hero writes them out the same way.
 */
const ENTRANCE = [
  "animate-fade-up",
  "animate-fade-up [animation-delay:120ms]",
  "animate-fade-up [animation-delay:240ms]",
] as const;

const SECTIONS = [
  { id: "colours", label: "Colours" },
  { id: "typography", label: "Typography" },
  { id: "shape", label: "Shape & depth" },
  { id: "motion", label: "Motion" },
  { id: "components", label: "Components" },
  { id: "feedback", label: "Feedback & status" },
  { id: "forms", label: "Forms" },
  { id: "grids", label: "Grids & cards" },
  { id: "furniture", label: "Page furniture" },
  { id: "tables", label: "Tables" },
  { id: "combinations", label: "Put together" },
] as const;

/**
 * The tokens this guide claims to document, each paired with the utility class
 * that is supposed to render it. Written out in full — Tailwind only keeps a
 * class name it can read literally in the source.
 */
const TOKEN_PROBES = [
  { token: "--color-primary", cls: "text-primary" },
  { token: "--color-secondary", cls: "text-secondary" },
  { token: "--color-accent", cls: "text-accent" },
  { token: "--color-success", cls: "text-success" },
  { token: "--color-info", cls: "text-info" },
  { token: "--color-warning", cls: "text-warning" },
  { token: "--color-error", cls: "text-error" },
] as const;

/**
 * Checks that the guide is telling the truth.
 *
 * A missing colour fails silently in the worst possible way: if the utility was
 * never generated, `text-error` is simply an unknown class, the element quietly
 * inherits the body ink, and the page looks deliberate. That is how a swatch
 * can sit in the palette above while nothing on the page actually wears it —
 * and the reader is left arguing with a page that looks fine.
 *
 * So the page measures itself: render each utility, read back what the browser
 * computed, and compare it with the token it is meant to come from. Anything
 * that does not match says so, loudly, right at the top.
 */
function TokenSelfCheck() {
  const probeRef = React.useRef<HTMLDivElement | null>(null);
  const [broken, setBroken] = React.useState<{ token: string; cls: string; why: string }[]>([]);

  React.useEffect(() => {
    const host = probeRef.current;
    if (!host) return;
    const root = getComputedStyle(document.documentElement);
    const found: { token: string; cls: string; why: string }[] = [];

    TOKEN_PROBES.forEach(({ token, cls }, i) => {
      const raw = root.getPropertyValue(token).trim();
      const el = host.children[i] as HTMLElement | undefined;
      if (!el) return;
      const got = getComputedStyle(el).color;
      if (!raw) {
        // No token at all. Fine if nothing claims to use it; a defect the
        // moment the palette above lists it as a colour of this site.
        found.push({ token, cls, why: "the theme defines no such token" });
        return;
      }
      const want = `rgb(${raw.split(/\s+/).join(", ")})`;
      if (got !== want) {
        found.push({
          token,
          cls,
          why: `${cls} renders ${got}, but ${token} is ${want}`,
        });
      }
    });
    setBroken(found);
  }, []);

  return (
    <>
      {/* Off-screen, not display:none — a hidden element still computes colour,
          but only if it is actually laid out. */}
      <div ref={probeRef} aria-hidden="true" className="pointer-events-none absolute -left-[9999px] top-0">
        {TOKEN_PROBES.map(({ cls, token }) => (
          <span key={token} className={cls}>
            .
          </span>
        ))}
      </div>
      {broken.length > 0 && (
        <Container className="pt-6">
          <Banner variant="error" title="This page is not showing you the real thing">
            <p>
              The stylesheet the browser actually loaded does not match the tokens this
              guide is reading. Until that is fixed, treat every specimen below as
              unreliable — the usual cause is a stale build, so a hard refresh is worth
              trying first.
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {broken.map((b) => (
                <li key={b.token}>
                  <code className="text-ink">{b.token}</code> — {b.why}
                </li>
              ))}
            </ul>
          </Banner>
        </Container>
      )}
    </>
  );
}

function Heading({ id, title, blurb }: { id: string; title: string; blurb: string }) {
  return (
    <div id={id} className="scroll-mt-36">
      <h2 className="font-heading text-2xl font-extrabold text-ink sm:text-3xl">{title}</h2>
      <p className="mt-2 max-w-3xl leading-relaxed text-muted">{blurb}</p>
    </div>
  );
}

/** A labelled specimen: the component name, then the component itself. */
function Specimen({
  name,
  note,
  children,
}: {
  name: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-card border border-border bg-card p-5 shadow-soft">
      <div className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-border pb-3">
        <code className="font-mono text-sm font-bold text-primary">{name}</code>
        {note && <span className="text-xs text-muted">{note}</span>}
      </div>
      {children}
    </div>
  );
}

function TokenName({ children }: { children: React.ReactNode }) {
  return <code className="block break-all font-mono text-xs font-semibold text-ink">{children}</code>;
}

function TokenValue({ children }: { children: React.ReactNode }) {
  return <code className="block break-all font-mono text-[11px] leading-snug text-muted">{children}</code>;
}

/** One colour, plus the text colour that belongs on it when there is one. */
function ColourCard({ base, fg }: { base: Token; fg?: Token }) {
  return (
    <div className="overflow-hidden rounded-card border border-border bg-card shadow-soft">
      <div
        // The inset hairline keeps near-white colours (paper, card) from
        // disappearing into the swatch card behind them.
        className="flex h-24 items-center justify-center ring-1 ring-inset ring-ink/15"
        style={{ backgroundColor: paint(base), color: fg ? paint(fg) : undefined }}
      >
        {fg && <span className="font-heading text-xl font-bold">Aa</span>}
      </div>
      <div className="space-y-1 p-3">
        <TokenName>{base.name}</TokenName>
        <TokenValue>{base.value}</TokenValue>
        {fg && (
          <div className="mt-3 border-t border-border pt-2">
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted">
              Text on it
            </p>
            <TokenName>{fg.name}</TokenName>
            <TokenValue>{fg.value}</TokenValue>
          </div>
        )}
      </div>
    </div>
  );
}

/** True while the motion specimens are allowed to move. */
const MotionStageContext = React.createContext(true);

/**
 * The stage the motion specimens stand on.
 *
 * Someone whose computer is set to reduce motion gets a still page everywhere
 * on this site — including, unhelpfully, on the one page whose whole job is to
 * show what the motion is. A guide that renders four dead boxes to the person
 * choosing the animations is worse than no guide at all, so it says what is
 * happening and offers to play them anyway.
 *
 * The opt-in adds `.motion-stage`, which is the single exception carved out of
 * the site-wide rule in theme.css. It is never on by default, and no live page
 * uses it.
 */
function MotionStage({ children }: { children: React.ReactNode }) {
  const [prefersReduced, setPrefersReduced] = React.useState(false);
  const [playAnyway, setPlayAnyway] = React.useState(false);

  React.useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setPrefersReduced(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);

  const playing = !prefersReduced || playAnyway;

  return (
    <MotionStageContext.Provider value={playing}>
      {prefersReduced && (
        <Banner
          variant={playAnyway ? "info" : "warning"}
          title={
            playAnyway
              ? "Playing the motion anyway"
              : "Your device is set to reduce motion, so nothing below is moving"
          }
          className="mt-8"
        >
          <p>
            {playAnyway
              ? "The specimens below are running as they would for someone who has not asked for less movement. Everything else on the site stays still."
              : "That setting is being honoured, which is why the specimens look frozen — this is the site behaving correctly, not a fault. To judge the movement you are specifying, you can run it here without changing anything else."}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => setPlayAnyway((on) => !on)}
          >
            {playAnyway ? "Stop and respect my setting" : "Play the motion anyway"}
          </Button>
        </Banner>
      )}
      <div className={cn("mt-8 space-y-6", playAnyway && "motion-stage")}>{children}</div>
    </MotionStageContext.Provider>
  );
}

/**
 * The banner in a box of its own, so its scroll effect can be watched without
 * the guide scrolling away underneath it. `scrollRoot` tells the real Hero to
 * measure itself against this frame instead of the window — same component,
 * same sums, smaller frame of reference.
 */
function ParallaxDemo() {
  const playing = React.useContext(MotionStageContext);
  const frameRef = React.useRef<HTMLDivElement | null>(null);
  const [reading, setReading] = React.useState({ picture: 0, words: 0, visible: 100 });

  React.useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    let raf = 0;
    const read = () => {
      raf = 0;
      const hero = frame.querySelector("section");
      if (!hero) return;
      const rect = hero.getBoundingClientRect();
      const top = rect.top - frame.getBoundingClientRect().top;
      const scrolled = Math.min(Math.max(-top, 0), rect.height);
      setReading({
        picture: Math.round(scrolled * HERO_PARALLAX.background),
        words: Math.round(scrolled * HERO_PARALLAX.copy),
        visible: Math.round(
          Math.max(0, 1 - scrolled / (rect.height * HERO_PARALLAX.fadeBy)) * 100,
        ),
      });
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(read);
    };
    read();
    frame.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      frame.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);


  // Nobody thinks to scroll inside a box on a page they are already scrolling.
  // So the specimen shows itself: the first time it comes into view it scrolls
  // down and back once, unprompted, and the reader can then take over. Held
  // back when the stage is still, so it never moves against the setting.
  const demonstrated = React.useRef(false);
  React.useEffect(() => {
    const frame = frameRef.current;
    if (!frame || !playing) return;
    let back = 0;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || demonstrated.current) continue;
          demonstrated.current = true;
          frame.scrollTo({ top: 240, behavior: "smooth" });
          back = window.setTimeout(() => frame.scrollTo({ top: 0, behavior: "smooth" }), 1600);
        }
      },
      { threshold: 0.6 },
    );
    observer.observe(frame);
    return () => {
      observer.disconnect();
      if (back) window.clearTimeout(back);
    };
  }, [playing]);

  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
        ↕ Scroll inside this box — the page behind it stays put
      </p>
      <div
        ref={frameRef}
        data-parallax-frame
        className="h-[420px] overflow-y-auto overscroll-contain rounded-card border border-border"
      >
        <Hero
          scrollRoot={frameRef}
          forceMotion={playing}
          eyebrow="Since 1861"
          title="A summer for Year 12 leavers"
          description="Come as you are. Hear from the Bible, meet the people, stay for morning tea."
          backgroundMedia={
            // A stand-in for a photograph: the live banner has no picture
            // behind it yet, and without one there is nothing for the
            // background layer to move.
            <div className="h-full w-full bg-gradient-to-br from-primary via-accent to-primary text-primary-fg/30">
              <WorldMapMotif className="h-full w-full" />
            </div>
          }
        />
        <div className="space-y-4 bg-paper p-6 text-sm leading-relaxed text-muted">
          <p className="font-semibold text-ink">Keep scrolling inside this box.</p>
          <p>
            The picture behind the banner is being left behind by the words, and the words
            are drifting down and fading as they go. Scroll back up and it all comes home.
          </p>
          <p>
            This is exactly what the top of the home page does — the difference is only that
            here the movement is bottled up in a box instead of taking the whole window.
          </p>
          <p>
            The banner is about 450 pixels tall, and the words are gone by the time you are
            85 per cent of the way past it — so keep going until the readout below says nought
            per cent, then scroll back up.
          </p>
          <p>
            Nothing else on this site moves as you scroll. There is no reveal-as-you-go on
            the cards or anywhere else, and none should be added without a reason better than
            novelty.
          </p>
          <p>
            The office pages, the service times, the growth groups — all of them simply sit
            where they are put. That is the house style, and this box is the one exception
            worth having.
          </p>
          <p>
            Scroll back to the top of this box and the banner puts itself back together
            exactly as it was — nothing here is one-way.
          </p>
        </div>
      </div>
      <dl
        aria-live="off"
        className="mt-3 grid grid-cols-3 gap-3 rounded-card border border-border bg-paper p-3 font-mono text-xs text-muted"
      >
        <div>
          <dt className="text-[10px] uppercase tracking-wide">Picture moved</dt>
          <dd className="text-ink" data-readout="picture">{reading.picture}px</dd>
        </div>
        <div>
          <dt className="text-[10px] uppercase tracking-wide">Words moved</dt>
          <dd className="text-ink" data-readout="words">{reading.words}px</dd>
        </div>
        <div>
          <dt className="text-[10px] uppercase tracking-wide">Words visible</dt>
          <dd className="text-ink" data-readout="visible">{reading.visible}%</dd>
        </div>
      </dl>
    </div>
  );
}

/** Leaves the page and comes straight back, so the scroll behaviour is seen. */
function PageChangeDemo() {
  const navigate = useNavigate();
  const [away, setAway] = React.useState(false);

  return (
    <div className="flex flex-wrap items-center gap-4">
      <Button
        variant="outline"
        disabled={away}
        onClick={() => {
          setAway(true);
          void navigate({ to: "/faq" }).then(() => {
            window.setTimeout(() => {
              void navigate({ to: "/design", hash: "motion" }).then(() => setAway(false));
            }, 1200);
          });
        }}
      >
        {away ? (
          <>
            <Spinner size="sm" label="Changing pages" />
            Off to the About page…
          </>
        ) : (
          "Go to About and come straight back"
        )}
      </Button>
      <span className="text-sm text-muted">
        Watch where you land: the top of About, then back to this very section.
      </span>
    </div>
  );
}

/** Sample cards for the CardCarousel specimen — content is illustrative only. */
const CAROUSEL_SAMPLES = [
  { id: "a", tag: "Workshop", title: "Holiday writing camp", note: "Three mornings of story craft" },
  { id: "b", tag: "Weekly", title: "Saturday small groups", note: "Six students, one tutor" },
  { id: "c", tag: "Event", title: "Open day", note: "Meet the team, see a class" },
  { id: "d", tag: "Talk", title: "Parent evening", note: "How the program works" },
  { id: "e", tag: "Workshop", title: "Debate intensive", note: "Argue well, listen better" },
];

function CarouselSpecimen() {
  return (
    <CardCarousel
      items={CAROUSEL_SAMPLES}
      getKey={(s) => s.id}
      label="Sample cards"
      renderItem={(s) => (
        <div className="h-full rounded-card border border-border bg-paper p-5">
          <Badge variant="secondary">{s.tag}</Badge>
          <p className="mt-3 font-heading text-lg font-bold text-heading">{s.title}</p>
          <p className="mt-1 text-sm text-muted">{s.note}</p>
        </div>
      )}
    />
  );
}

function RevealSpecimen() {
  return (
    <RevealGroup as="ul" className="grid gap-4 sm:grid-cols-3">
      {["Arrives first", "Then this", "Then this"].map((text, i) => (
        <Reveal as="li" key={i} className="rounded-card border border-border bg-paper p-5">
          <p className="font-heading font-bold text-heading">{text}</p>
          <p className="mt-1 text-sm text-muted">Reveal #{i + 1} in a RevealGroup</p>
        </Reveal>
      ))}
    </RevealGroup>
  );
}

/**
 * Runs an animation again on demand. Re-mounting the children with a new key is
 * what restarts a CSS animation — there is no "play it again" switch.
 */
function Replay({ children }: { children: React.ReactNode }) {
  const [run, setRun] = React.useState(0);
  const hostRef = React.useRef<HTMLDivElement | null>(null);
  const played = React.useRef(false);

  // A one-shot entrance fires on mount — which, for a specimen sitting three
  // screens below the fold, means it has long finished by the time anyone
  // scrolls down to it. All you ever see is the still frame at the end, and
  // "the animation is broken" is the only reasonable conclusion to draw. So
  // hold it until it is actually on screen, then start it once.
  React.useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || played.current) continue;
          played.current = true;
          setRun((n) => n + 1);
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={hostRef}>
      <div key={run}>{children}</div>
      <Button
        variant="outline"
        size="sm"
        className="mt-5"
        onClick={() => setRun((n) => n + 1)}
      >
        Play it again
      </Button>
    </div>
  );
}

/**
 * A banner that can actually be dismissed, so the dismiss button does
 * something real. Bringing it back is a courtesy of this page only — a real
 * page would simply lose the banner.
 */
function DismissableBanner() {
  const [dismissed, setDismissed] = React.useState(false);

  if (dismissed) {
    return (
      <Button variant="outline" size="sm" onClick={() => setDismissed(false)}>
        Bring the dismissed banner back
      </Button>
    );
  }

  return (
    <Banner
      variant="success"
      title="Your details are with us"
      onDismiss={() => setDismissed(true)}
    >
      Someone from the office will be in touch this week.
    </Banner>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * The page
 * ═════════════════════════════════════════════════════════════════════════ */

/**
 * The COMPONENT STYLE GUIDE — the "Components" tab of /design (the brand
 * board is the other tab: packages/brand/src/board). Every token read live
 * from the document and every @alight/ui component wearing the current look.
 * Add a component to packages/ui → add its specimen here, in every app.
 */
export function DesignGuide() {

  const theme = useLiveTheme();
  const value = (name: string) => theme.values[name] ?? "not set";

  // Pair each colour with its matching "-fg" (the text colour meant to sit on
  // it) so the two are always read together.
  const byName = new Map(theme.colours.map((token) => [token.name, token]));
  const colourGroups = theme.colours
    .filter((token) => !(token.name.endsWith("-fg") && byName.has(token.name.slice(0, -3))))
    .map((token) => ({ base: token, fg: byName.get(`${token.name}-fg`) }));

  const fontSans = value("--font-sans");
  const fontHeading = value("--font-heading");

  return (
    <>
      <PageHeader
        eyebrow="Internal reference"
        title="Design system"
        description="Everything the Connect Sydney site is built from — every colour, every size of type, the corners and shadows, and every component wearing the look that is switched on right now. Try a new palette and this page changes with it."
      />

      <TokenSelfCheck />

      {/* Jump links. Sits just under the site's own header (h-16 / top-16). */}
      <div className="sticky top-[109px] z-20 border-b border-border bg-card/95 backdrop-blur">
        <Container className="flex flex-wrap items-center gap-x-5 gap-y-1 py-3">
          <span className="text-xs font-bold uppercase tracking-wide text-muted">Jump to</span>
          {SECTIONS.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              className="text-sm font-semibold text-primary hover:underline"
            >
              {section.label}
            </a>
          ))}
        </Container>
      </div>

      {/* ── a. Colours ─────────────────────────────────────────────────── */}
      <Section tone="paper" spacing="lg">
        <Heading
          id="colours"
          title="Colours"
          blurb="Read straight from the live page, so any colour added to the theme turns up here by itself. Each swatch shows the block of colour, the name to use for it, and the red-green-blue numbers behind it."
        />

        {colourGroups.length === 0 ? (
          <p className="mt-8 text-muted">Reading the theme…</p>
        ) : (
          <>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {colourGroups.map((group) => (
                <ColourCard key={group.base.name} base={group.base} fg={group.fg} />
              ))}
            </div>
            <p className="mt-6 text-sm text-muted">
              {colourGroups.length} colour{colourGroups.length === 1 ? "" : "s"} in the theme
              right now ({theme.colours.length} tokens in total, counting the text colours
              paired with them).
            </p>
          </>
        )}

        {/* Every colour also comes in see-through strengths. */}
        <div className="mt-10">
          <h3 className="font-heading text-lg font-bold text-ink">Strengths</h3>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted">
            Any colour can be softened — used at a fraction of its full strength for tints,
            hover states and quiet backgrounds.
          </p>
          <div className="mt-4 space-y-2">
            {/* The percentages are labelled once, above the ramps — printed on
                the swatches themselves they vanish into the darker steps. */}
            <div className="flex items-center gap-3">
              <span className="w-44 shrink-0" />
              <div className="flex flex-1">
                {STRENGTHS.map((strength) => (
                  <span
                    key={strength}
                    className="flex-1 text-center font-mono text-[10px] text-muted"
                  >
                    {strength}%
                  </span>
                ))}
              </div>
            </div>
            {colourGroups.map((group) => (
              <div key={`ramp-${group.base.name}`} className="flex items-center gap-3">
                <code className="w-44 shrink-0 truncate font-mono text-xs text-muted">
                  {group.base.name}
                </code>
                <div className="flex flex-1 overflow-hidden rounded border border-border">
                  {STRENGTHS.map((strength) => (
                    <div
                      key={strength}
                      className="h-8 flex-1"
                      style={{
                        backgroundColor: CHANNELS.test(group.base.value)
                          ? `rgb(var(${group.base.name}) / ${strength / 100})`
                          : paint(group.base),
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── b. Typography ──────────────────────────────────────────────── */}
      <Section tone="card" spacing="lg">
        <Heading
          id="typography"
          title="Typography"
          blurb="Two type families do all the work: one for headings, one for everything else. Both are read from the live theme."
        />

        <div className="mt-8 grid gap-5 sm:grid-cols-2">
          <div className="rounded-card border border-border bg-paper p-5">
            <p className="text-xs font-bold uppercase tracking-wide text-muted">Headings</p>
            <p className="mt-1 font-heading text-2xl font-extrabold text-ink">
              {leadFamily(fontHeading)}
            </p>
            <TokenName>--font-heading</TokenName>
            <TokenValue>{fontHeading}</TokenValue>
          </div>
          <div className="rounded-card border border-border bg-paper p-5">
            <p className="text-xs font-bold uppercase tracking-wide text-muted">Body text</p>
            <p className="mt-1 text-2xl font-bold text-ink">{leadFamily(fontSans)}</p>
            <TokenName>--font-sans</TokenName>
            <TokenValue>{fontSans}</TokenValue>
          </div>
        </div>

        <div className="mt-8 space-y-6 rounded-card border border-border bg-paper p-6">
          <div>
            <p className="mb-1 text-xs font-mono text-muted">Heading 1 · headings font</p>
            <h1 className="font-heading text-4xl font-extrabold leading-tight text-ink sm:text-5xl">
              A quiet welcome on a Sunday morning
            </h1>
          </div>
          <div>
            <p className="mb-1 text-xs font-mono text-muted">Heading 2 · headings font</p>
            <h2 className="font-heading text-3xl font-extrabold leading-tight text-ink">
              Gathering around the Bible
            </h2>
          </div>
          <div>
            <p className="mb-1 text-xs font-mono text-muted">Heading 3 · headings font</p>
            <h3 className="font-heading text-xl font-bold text-ink">Groups through the week</h3>
          </div>
          <div>
            <p className="mb-1 text-xs font-mono text-muted">Heading 4 · headings font</p>
            <h4 className="font-heading text-lg font-bold text-ink">Getting here</h4>
          </div>
          <div>
            <p className="mb-1 text-xs font-mono text-muted">Body · body font</p>
            <p className="max-w-2xl text-base leading-relaxed text-ink">
              Body copy is set in the body font at a comfortable reading size with generous
              line spacing. This is the paragraph style used for the bulk of every page, so
              it is the one to check first when the type family changes.
            </p>
          </div>
          <div>
            <p className="mb-1 text-xs font-mono text-muted">Small · body font</p>
            <p className="max-w-2xl text-sm leading-relaxed text-ink">
              Smaller supporting text — captions, fine print and helper lines under form
              fields.
            </p>
          </div>
          <div>
            <p className="mb-1 text-xs font-mono text-muted">Quiet · body font, muted colour</p>
            <p className="max-w-2xl text-sm leading-relaxed text-muted">
              Quiet text steps back from the page. It uses the muted colour and carries
              anything that should not compete with the main message.
            </p>
          </div>
        </div>
      </Section>

      {/* ── c. Shape & depth ───────────────────────────────────────────── */}
      <Section tone="paper" spacing="lg">
        <Heading
          id="shape"
          title="Shape & depth"
          blurb="How rounded the corners are and how much things lift off the page. Change these two pairs and the whole site changes character — square and traditional, or soft and friendly."
        />

        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-card border border-border bg-card p-5 shadow-soft">
            <div className="h-20 rounded bg-primary" />
            <p className="mt-4 text-sm font-semibold text-ink">Standard corner</p>
            <TokenName>--radius</TokenName>
            <TokenValue>{value("--radius")}</TokenValue>
          </div>
          <div className="rounded-card border border-border bg-card p-5 shadow-soft">
            <div className="h-20 rounded-card bg-primary" />
            <p className="mt-4 text-sm font-semibold text-ink">Card corner</p>
            <TokenName>--radius-card</TokenName>
            <TokenValue>{value("--radius-card")}</TokenValue>
          </div>
          <div className="rounded-card border border-border bg-card p-5">
            <div className="h-20 rounded-card bg-card shadow-soft ring-1 ring-inset ring-ink/5" />
            <p className="mt-4 text-sm font-semibold text-ink">Soft lift</p>
            <TokenName>--shadow-soft</TokenName>
            <TokenValue>{value("--shadow-soft")}</TokenValue>
          </div>
          <div className="rounded-card border border-border bg-card p-5">
            <div className="h-20 rounded-card bg-card shadow-card ring-1 ring-inset ring-ink/5" />
            <p className="mt-4 text-sm font-semibold text-ink">Card lift</p>
            <TokenName>--shadow-card</TokenName>
            <TokenValue>{value("--shadow-card")}</TokenValue>
          </div>
        </div>
      </Section>

      {/* ── d. Motion ──────────────────────────────────────────────────── */}
      <Section tone="card" spacing="lg">
        <Heading
          id="motion"
          title="Motion"
          blurb="There is very little movement on this site, and that is on purpose. Two entrance animations, a gentle drift on the banner as you scroll past it, and the small signs that something is loading — that is the lot. Every specimen below really runs — if one looks still and there is no notice saying why, it is broken."
        />

        <MotionStage>
          <Specimen name="Fade up" note="animate-fade-up · 0.7s · cubic-bezier(0.16, 1, 0.3, 1)">
            <Replay>
              <div className="grid gap-4 sm:grid-cols-3">
                {[
                  { title: "Morning Prayer", body: "8:00am" },
                  { title: "Family service", body: "9:30am" },
                  { title: "Evening service", body: "5:00pm" },
                ].map((item, index) => (
                  <div
                    key={item.title}
                    className={cn(
                      "rounded-card border border-border bg-paper p-5",
                      ENTRANCE[index],
                    )}
                  >
                    <p className="font-heading text-lg font-bold text-ink">{item.title}</p>
                    <p className="mt-1 text-sm text-muted">{item.body}</p>
                  </div>
                ))}
              </div>
            </Replay>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted">
              Things rise a centimetre and a half into place as they fade in. Where several
              arrive together they are staggered by an eighth of a second each, so the eye
              follows them in order instead of being handed the lot at once. The animation
              holds its starting state until its turn comes, so nothing flashes into view
              early.
            </p>
          </Specimen>

          <Specimen
            name="Hero zoom"
            note="animate-hero-zoom · 1.6s · the right-hand one is slowed on purpose"
          >
            <Replay>
              <div className="grid gap-4 sm:grid-cols-2">
                <figure>
                  <div className="overflow-hidden rounded-card border border-border">
                    <div className="animate-hero-zoom relative h-44 bg-primary text-primary-fg/40">
                      <WorldMapMotif className="absolute inset-0 h-full w-full" />
                    </div>
                  </div>
                  <figcaption className="mt-2 text-xs text-muted">
                    As it really is — 1.6 seconds
                  </figcaption>
                </figure>
                <figure>
                  <div className="overflow-hidden rounded-card border border-border">
                    <div className="animate-hero-zoom [animation-duration:5s] relative h-44 bg-primary text-primary-fg/40">
                      <WorldMapMotif className="absolute inset-0 h-full w-full" />
                    </div>
                  </div>
                  <figcaption className="mt-2 text-xs text-muted">
                    The same thing slowed to 5 seconds, so you can watch it
                  </figcaption>
                </figure>
              </div>
            </Replay>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted">
              Only the picture behind the banner uses this one. It starts eight per cent too
              large and settles back, which reads as the page arriving rather than as an
              effect. At full speed it is deliberately hard to catch — that is the point of
              it — so the second frame runs the identical animation three times slower.
            </p>
          </Specimen>

          <Specimen
            name="The banner as you scroll"
            note="Hero — parallax and fade, in a frame of its own"
          >
            <ParallaxDemo />
            <div className="mt-4 max-w-2xl space-y-3 text-sm leading-relaxed text-muted">
              <p>
                Scroll inside the box above — the page itself will stay put. The picture
                travels at {Math.round(HERO_PARALLAX.background * 100)} per cent of your
                scrolling speed, the words at{" "}
                {Math.round(HERO_PARALLAX.copy * 100)} per cent, and the words have faded out
                entirely by the time you are {Math.round(HERO_PARALLAX.fadeBy * 100)} per cent
                of the way down the banner. Together they give the top of the page a sense of
                depth. The three numbers under the box are read from the banner as you scroll
                it, and the numbers in this paragraph come from the same place the banner
                itself reads them — neither can drift from the real thing.
              </p>
              <p>
                This is the real banner, not a drawing of one. The specimen further up the
                page is the same component with the movement switched off
                (<code className="font-mono text-xs">animated={"{false}"}</code>): left on, it
                would fade itself out as you scrolled down to read the rest of the guide, and
                a reference sheet you cannot read is no use.
              </p>
            </div>
          </Specimen>

          <Specimen name="Spinner" note="a short wait, with nothing useful to show yet">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="flex items-center justify-center gap-6 rounded-card bg-primary p-8 text-primary-fg">
                <Spinner size="lg" />
                <Spinner />
                <Spinner size="sm" />
              </div>
              <div className="flex items-center justify-center gap-6 rounded-card border border-border bg-paper p-8 text-primary">
                <Spinner size="lg" />
                <Spinner />
                <Spinner size="sm" />
              </div>
              <div className="flex items-center justify-center rounded-card border border-border bg-paper p-8">
                <Button disabled>
                  <Spinner size="sm" label="Sending your message" />
                  Sending…
                </Button>
              </div>
            </div>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted">
              Half a ring, turning once a second. Half rather than a nearly-closed circle on
              purpose: a gap that big is unmistakably moving, where a thin arc just sits there
              looking like a comma. It is drawn in the colour of the text around it, so it works
              on the navy as well as on the paper, and inside a button it comes out in the
              button&apos;s own text colour. The three sizes are 16, 24 and 40 pixels — the small
              one beside words, the large one on its own.
            </p>
          </Specimen>

          <Specimen name="Skeleton" note="a wait where the shape of the answer is already known">
            <div className="grid gap-5 sm:grid-cols-2">
              <div role="status" aria-label="Loading service times">
                <div className="rounded-card border border-border bg-card p-6 shadow-soft">
                  <Skeleton className="h-5 w-20 rounded-full" />
                  <Skeleton className="mt-4 h-7 w-2/3" />
                  <Skeleton className="mt-4 h-4 w-full" />
                  <Skeleton className="mt-2 h-4 w-5/6" />
                  <Skeleton className="mt-2 h-4 w-2/5" />
                  <Skeleton className="mt-5 h-24 w-full" />
                  <Skeleton className="mt-5 h-9 w-28 rounded-card" />
                </div>
                <p className="mt-2 text-xs text-muted">While it is on its way</p>
              </div>
              <div>
                <Card className="flex h-full flex-col p-6">
                  <CardHeader className="p-0">
                    <Badge variant="accent" className="w-fit">
                      9:30am
                    </Badge>
                    <CardTitle className="mt-4 font-heading text-2xl">Family service</CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-1 flex-col p-0">
                    <p className="mt-4 text-sm leading-relaxed text-muted">
                      Singing, a Bible talk, and kids&apos; ministry running alongside. About
                      an hour and a quarter, with morning tea afterwards.
                    </p>
                    <div className="mt-5 h-24 rounded-card bg-primary/10" />
                    <div className="mt-5">
                      <Button variant="outline" size="sm">
                        Read more
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                <p className="mt-2 text-xs text-muted">What it turns into</p>
              </div>
            </div>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted">
              Shaped like the thing that is coming — a chip, a heading, lines of text of
              different lengths, a block, a button — so nothing jumps about when the real
              content lands. A band of light sweeps across to show it is still working. Use a
              skeleton or a spinner, never both in the same spot.
            </p>
          </Specimen>

          <Specimen name="Waiting in words" note="LoadingDots">
            <div className="rounded-card border border-border bg-paper px-4 py-10 text-center text-sm text-muted">
              Loading messages
              <LoadingDots />
            </div>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted">
              The plainest option, and often the right one. The three dots take it in turns to
              brighten so the panel never looks like a page that has simply stopped. They take
              the colour of the words around them, and a screen reader hears a single
              &quot;…&quot; rather than three full stops. The office pages say it this way
              today, with a still ellipsis; this is the piece to reach for when they are next
              touched.
            </p>
          </Specimen>

          <Specimen name="Moving between pages" note="what happens on a page change">
            <PageChangeDemo />
            <div className="mt-4 max-w-2xl space-y-3 text-sm leading-relaxed text-muted">
              <p>
                Every page is already in the browser by the time you click, so a page change
                happens at once — there is no in-between state, no spinner, no fade. What does
                happen is that the new page starts at the top: whatever you were reading, you
                arrive at the top of the next page.
              </p>
              <p>
                Going back does the same. The browser&apos;s back button returns you to the
                previous page at the top, not to the spot you left — measured, not assumed. If
                that ever needs to change, it is the router that decides it, not the page.
              </p>
            </div>
          </Specimen>

          <Specimen name="Reduced motion" note="when someone has asked for less movement">
            <p className="max-w-2xl text-sm leading-relaxed text-muted">
              Anyone whose device is set to reduce motion gets none of the above. One rule in
              the theme stills every animation and transition site-wide, and the banner's
              scroll effect never registers at all. That is the whole mechanism — components
              do not each decide for themselves, so a new animation is covered the moment it
              is written and there is no second switch to forget.
            </p>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted">
              This page is the one exception, and only when asked. If your own device has that
              setting on, a notice at the top of this section offers to play the specimens
              regardless — because choosing an animation you are forbidden to see is not a
              choice. Nothing outside this section moves, and the exception cannot reach a
              live page.
            </p>
          </Specimen>
        </MotionStage>
      </Section>

      {/* ── e. Components ──────────────────────────────────────────────── */}
      <Section tone="paper" spacing="lg">
        <Heading
          id="components"
          title="Components"
          blurb="Every reusable piece the site is built from, wearing the theme exactly as it is now. Change a colour and watch each one follow."
        />

        <div className="mt-8 space-y-6">
          <Specimen name="Button" note="every style and size">
            <div className="space-y-5">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
                  Styles
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <Button>Primary</Button>
                  <Button variant="secondary">Secondary</Button>
                  <Button variant="accent">Accent</Button>
                  <Button variant="outline">Outline</Button>
                  <Button variant="ghost">Ghost</Button>
                  <Button variant="link">Link</Button>
                </div>
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
                  Sizes
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <Button size="sm">Small</Button>
                  <Button>Standard</Button>
                  <Button size="lg">Large</Button>
                  <Button size="icon" aria-label="Icon button">
                    <BookOpen className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
                  Turned off
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <Button disabled>Unavailable</Button>
                  <Button variant="outline" disabled>
                    Unavailable
                  </Button>
                </div>
              </div>
            </div>
          </Specimen>

          <Specimen name="Badge" note="small status labels">
            <div className="flex flex-wrap items-center gap-3">
              <Badge>Default</Badge>
              <Badge variant="secondary">Secondary</Badge>
              <Badge variant="accent">Accent</Badge>
              <Badge variant="success">Success</Badge>
              <Badge variant="muted">Muted</Badge>
              <Badge variant="outline">Outline</Badge>
            </div>
          </Specimen>

          <Specimen
            name="Card"
            note="CardHeader · CardTitle · CardDescription · CardContent · CardFooter"
          >
            <Card className="max-w-md">
              <CardHeader>
                <CardTitle>Sunday morning</CardTitle>
                <CardDescription>Every week at 9:30am</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed text-muted">
                  A service with singing, prayer and a talk from the Bible, with children&apos;s
                  ministry running alongside.
                </p>
              </CardContent>
              <CardFooter className="gap-3">
                <Button size="sm">Plan a visit</Button>
                <Button size="sm" variant="ghost">
                  Service times
                </Button>
              </CardFooter>
            </Card>
          </Specimen>

          <Specimen name="Input · Textarea · Label · Select" note="form fields">
            <div className="grid max-w-2xl gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="ds-name">Your name</Label>
                <Input id="ds-name" placeholder="Jane Smith" defaultValue="" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ds-email">Email</Label>
                <Input id="ds-email" type="email" placeholder="jane@example.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ds-service">Which service?</Label>
                <Select>
                  <SelectTrigger id="ds-service">
                    <SelectValue placeholder="Choose a service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="8am">8:00am · Prayer Book</SelectItem>
                      <SelectItem value="930am">9:30am · Family service</SelectItem>
                      <SelectItem value="5pm">5:00pm · Evening</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ds-disabled">Turned off</Label>
                <Input id="ds-disabled" defaultValue="Not editable" disabled />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="ds-message">Message</Label>
                <Textarea id="ds-message" placeholder="How can we help?" />
              </div>
            </div>
          </Specimen>

          <Specimen name="Accordion" note="AccordionItem · AccordionTrigger · AccordionContent">
            <Accordion type="single" collapsible defaultValue="one" className="max-w-2xl">
              <AccordionItem value="one">
                <AccordionTrigger>Where do you meet?</AccordionTrigger>
                <AccordionContent>
                  A short walk from the station — there is parking nearby and someone
                  at the door to point you in the right direction.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="two">
                <AccordionTrigger>What should I wear?</AccordionTrigger>
                <AccordionContent>
                  Whatever you are comfortable in. You will see everything from suits to
                  shorts.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Specimen>

          <Specimen name="Table" note="TableHeader · TableBody · TableRow · TableHead · TableCell">
            <Table className="max-w-2xl">
              <TableHeader>
                <TableRow>
                  <TableHead>Service</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Style</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell>Morning Prayer</TableCell>
                  <TableCell>8:00am</TableCell>
                  <TableCell>Prayer Book</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Family service</TableCell>
                  <TableCell>9:30am</TableCell>
                  <TableCell>All ages</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Evening service</TableCell>
                  <TableCell>5:00pm</TableCell>
                  <TableCell>Contemporary</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Specimen>

          <Specimen
            name="Logo"
            note="LogoMark · Logo — the two-tone wordmark, recoloured by the theme"
          >
            <div className="flex flex-wrap items-end gap-8">
              <figure>
                <LogoMark className="text-4xl" />
                <figcaption className="mt-2 text-xs text-muted">wordmark</figcaption>
              </figure>
              <figure>
                <div className="rounded-card bg-primary p-3">
                  <LogoMark tone="mono" className="text-4xl text-primary-fg" />
                </div>
                <figcaption className="mt-2 text-xs text-muted">mono, on the brand band</figcaption>
              </figure>
              <figure>
                <Logo />
                <figcaption className="mt-2 text-xs text-muted">the full lockup</figcaption>
              </figure>
            </div>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted">
              The original site&apos;s mark is the word CONNECT in two blues with the N&apos;s
              overlapping. It is set as type rather than the ripped JPEG so it scales cleanly and
              recolours with the theme: the two tones come from CSS variables, which is how the
              Studio can draw each brand option wearing its own colours.
            </p>
          </Specimen>

          <Specimen
            name="Dropdown menu"
            note="DropdownMenu · Trigger · Content · Item — the Studio's own Iterations menu"
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">Choose an iteration</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuLabel>Recent rounds</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="flex-col items-start gap-0.5">
                  <span className="font-semibold">Iteration 2 — 1 option  (latest)</span>
                  <span className="text-muted">Applied theme</span>
                </DropdownMenuItem>
                <DropdownMenuItem className="flex-col items-start gap-0.5">
                  <span className="font-semibold">Iteration 1 — 4 options</span>
                  <span className="text-muted">Teal &amp; Gold, Wine &amp; Gold, Sage &amp; Coral</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted">
              A menu for picking one of several things, where a row needs more than a single
              line. The Studio's branding bar uses exactly this for its Iterations list — the
              Studio chrome is built from the same components as the site, so it wears
              whatever look is being tried on rather than sitting in fixed greys beside it.
            </p>
          </Specimen>

          <Specimen name="Dialog" note="press the button to open it">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline">Open the dialog</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Planning a visit?</DialogTitle>
                  <DialogDescription>
                    Tell us you are coming and someone will meet you at the door.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                  <Label htmlFor="ds-dialog-name">Your name</Label>
                  <Input id="ds-dialog-name" placeholder="Jane Smith" />
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="ghost">Not now</Button>
                  </DialogClose>
                  <DialogClose asChild>
                    <Button>Let them know</Button>
                  </DialogClose>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </Specimen>

          <Specimen
            name="CardCarousel"
            note="Carousel · CarouselContent · CarouselItem · CarouselPrevious · CarouselNext underneath"
          >
            <CarouselSpecimen />
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted">
              A row of cards that swipes. On a phone one card shows with the next peeking in;
              two across from a tablet, three on a laptop. Drag, swipe, use the arrow keys once
              focus is inside, or the buttons — which stop at either end and disappear when
              every card already fits. It never moves on its own.
            </p>
          </Specimen>

          <Specimen name="Reveal · RevealGroup" note="a once-only fade-up as content scrolls into view">
            <RevealSpecimen />
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted">
              These rose into place as you scrolled down to them, one after another. Only
              content that starts below the fold does this — anything already on screen just
              shows, the page's HTML always has every word in it, and if your device asks for
              less movement nothing moves at all (so with that setting on, these simply sit
              here).
            </p>
          </Specimen>

          <Specimen name="PillarCard" note="icon tiles, usually three across">
            <div className="grid gap-5 sm:grid-cols-3">
              <PillarCard
                icon={<BookOpen className="h-5 w-5" />}
                title="The Bible taught"
                description="Every service works through a passage, plainly and carefully."
              />
              <PillarCard
                icon={<Users className="h-5 w-5" />}
                title="Jesus | People | Campus"
                description="Small groups through the week where people are genuinely known."
              />
              <PillarCard
                icon={<HandHeart className="h-5 w-5" />}
                title="Care in the Hills"
                description="Practical help for our neighbours, offered without strings."
              />
            </div>
          </Specimen>

          <Specimen name="Container" note="the page gutter — width: narrow · default · wide">
            <div className="space-y-2">
              {(["narrow", "default", "wide"] as const).map((width) => (
                <Container key={width} width={width} className="bg-primary/10 py-2">
                  <span className="font-mono text-xs text-primary">width=&quot;{width}&quot;</span>
                </Container>
              ))}
            </div>
          </Specimen>

          <Specimen name="Section" note="a band of page — tone: paper · card · primary">
            <div className="space-y-3 overflow-hidden rounded-card border border-border">
              {(["paper", "card", "primary"] as const).map((tone) => (
                <Section key={tone} tone={tone} spacing="sm">
                  <span className="font-mono text-xs">tone=&quot;{tone}&quot;</span>
                </Section>
              ))}
            </div>
          </Specimen>

          <Specimen name="PageHeader" note="the heading band at the top of an inner page">
            <div className="overflow-hidden rounded-card border border-border">
              <PageHeader
                eyebrow="Our life together"
                title="Sunday services"
                description="Three services each Sunday, all built around the Bible read and taught."
                actions={<Button variant="outline">Plan a visit</Button>}
              />
            </div>
          </Specimen>

          <Specimen
            name="Hero"
            note="the big banner — the moving effects are switched off here so it stays readable"
          >
            <div className="overflow-hidden rounded-card border border-border">
              <Hero
                animated={false}
                eyebrow="Since 1861"
                title="A summer for Year 12 leavers"
                description="Come as you are. Hear from the Bible, meet the people, stay for morning tea."
                actions={
                  <>
                    <Button variant="secondary">Service times</Button>
                    <Button variant="outline" className="border-primary-fg/40 text-primary-fg">
                      About us
                    </Button>
                  </>
                }
              />
            </div>
          </Specimen>

          <Specimen name="WorldMapMotif" note="the decorative backdrop mark">
            <div className="rounded-card bg-primary p-6 text-primary-fg/25">
              <WorldMapMotif className="h-40 w-full" />
            </div>
          </Specimen>
        </div>
      </Section>

      {/* ── f. Feedback & status ───────────────────────────────────────── */}
      <Section tone="card" spacing="lg">
        <Heading
          id="feedback"
          title="Feedback & status"
          blurb="How the site tells someone what just happened. Info is a plain notice; success means it worked; warning means something needs attention but nothing is broken; error means something failed. Pick by what is true, not by how loud you want to be."
        />

        <div className="mt-8 space-y-6">
          <Specimen name="Banner" note="a message in the flow of the page">
            <div className="max-w-2xl space-y-4">
              <Banner>
                The 8:00am service meets in the hall this Sunday while the main building
                is being repainted.
              </Banner>
              <DismissableBanner />
              <Banner variant="warning" title="Parking is tight this Sunday">
                The Windsor Road car park is closed for works, so please allow a few extra
                minutes to find a spot.
              </Banner>
              <Banner variant="error" title="That message did not send">
                Something went wrong at our end. Please try again, or ring the office on
                (02) 9686 3643.
              </Banner>
            </div>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted">
              Each one carries its own icon as well as its own colour, so the meaning still
              comes across to someone who cannot tell the colours apart. Warnings and
              errors are read out straight away by a screen reader; the calmer two wait
              their turn.
            </p>
          </Specimen>

          <Specimen name="Badge" note="the same four meanings, in a word">
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="success">Replied</Badge>
              <Badge variant="warning">Waiting on us</Badge>
              <Badge variant="error">Could not send</Badge>
              <Badge variant="muted">Archived</Badge>
            </div>
          </Specimen>

          <Specimen
            name="Field error"
            note="Label · Input · the message underneath — shown beside a resting field"
          >
            <div className="grid max-w-xl gap-6 sm:grid-cols-2">
              {/* Side by side, because "what does an error look like" is really
                  "what does it look like compared with a field that is fine". */}
              <div className="space-y-2">
                <Label htmlFor="ds-ok-email">Email</Label>
                <Input id="ds-ok-email" type="email" defaultValue="jane@example.com" />
                <p className="text-sm text-muted">At rest — border, no message.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ds-error-email">Email</Label>
                <Input
                  id="ds-error-email"
                  type="email"
                  defaultValue="jane@"
                  aria-invalid
                  aria-describedby="ds-error-email-message"
                />
                <p
                  id="ds-error-email-message"
                  className="flex items-start gap-1.5 text-sm font-medium text-error"
                >
                  <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>That email address does not look complete — check for a typo.</span>
                </p>
              </div>
            </div>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted">
              The message sits directly under the field it belongs to, and the two are tied
              together so a screen reader reads the message when the field is reached. The
              field carries the error colour on its border, on a ring just outside it and as
              a wash across the field itself — a 1px hairline that only changes hue is the
              same weight as the resting state and reads as no change at all. The icon means
              colour is never the only thing saying something is wrong.
            </p>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted">
              All of it comes from the one <code className="text-ink">aria-invalid</code>{" "}
              flag a form already sets, on inputs, textareas and selects alike, so no page
              has to remember to style it.
            </p>
          </Specimen>
        </div>
      </Section>

      {/* ── g. Forms ───────────────────────────────────────────────────── */}
      <Section tone="paper" spacing="lg">
        <Heading
          id="forms"
          title="Forms"
          blurb="The shape every form on the site follows: the label above the field, a hint under it where one helps, and anything that went wrong directly beneath the field it belongs to. This is the contact form, laid out piece by piece."
        />

        <div className="mt-8 space-y-6">
          <Specimen name="Full form" note="the contact page pattern, stacked">
            <form className="max-w-2xl space-y-4" onSubmit={(event) => event.preventDefault()}>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="ds-form-name">
                    Name <span className="font-normal text-muted">(optional)</span>
                  </Label>
                  <Input id="ds-form-name" autoComplete="name" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ds-form-email">
                    Email <span className="font-normal text-muted">(optional)</span>
                  </Label>
                  <Input id="ds-form-email" type="email" autoComplete="email" />
                  <p className="text-xs text-muted">
                    Only needed if you would like someone to write back.
                  </p>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="ds-form-phone">
                    Phone <span className="font-normal text-muted">(optional)</span>
                  </Label>
                  <Input id="ds-form-phone" type="tel" autoComplete="tel" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ds-form-service">
                    Which service <span className="font-normal text-muted">(optional)</span>
                  </Label>
                  <Select>
                    <SelectTrigger id="ds-form-service">
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="8am">8:00am · Prayer Book</SelectItem>
                        <SelectItem value="930am">9:30am · Family service</SelectItem>
                        <SelectItem value="5pm">5:00pm · Evening</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ds-form-message">Message</Label>
                <Textarea
                  id="ds-form-message"
                  rows={5}
                  defaultValue=""
                  aria-invalid
                  aria-describedby="ds-form-message-error"
                />
                <p id="ds-form-message-error" className="text-sm font-medium text-error">
                  Please write a message.
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button type="submit">Send message</Button>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </div>
            </form>

            <div className="mt-5 max-w-2xl space-y-2 border-t border-border pt-4 text-sm leading-relaxed text-muted">
              <p>
                Fields sit in a <code className="font-mono text-xs">space-y-1.5</code> stack
                and pair up two across on anything wider than a phone. Optional fields say so
                in the label rather than marking the required ones — most of this form is
                optional.
              </p>
              <p>
                Two honest notes. The live contact form has no cancel button; the only
                second action it offers is &quot;Send another message&quot; once a message
                has gone. And there is no tick-box or radio button in the kit yet — no page
                has needed one, so none has been drawn.
              </p>
            </div>
          </Specimen>

          <Specimen name="Inline form" note="one field and a button, side by side">
            <form
              className="flex max-w-lg flex-col gap-3 sm:flex-row"
              onSubmit={(event) => event.preventDefault()}
            >
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="ds-inline-email" className="sr-only">
                  Email
                </Label>
                <Input id="ds-inline-email" type="email" placeholder="you@example.com" />
              </div>
              <Button type="submit" className="shrink-0">
                Send me the notices
              </Button>
            </form>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted">
              For a single question where a whole form would be too much. The label is still
              there for anyone using a screen reader, just not shown. No live page uses this
              one yet — it is here for when a sign-up strip is wanted.
            </p>
          </Specimen>
        </div>
      </Section>

      {/* ── h. Grids & cards ───────────────────────────────────────────── */}
      <Section tone="card" spacing="lg">
        <Heading
          id="grids"
          title="Grids & cards"
          blurb="The three arrangements the site repeats: three across for the Sunday services, two across for the bigger features, and a stack of rows for a plain list. All are shown full width, so you can drag the window narrow and watch them fold down."
        />

        <div className="mt-8 space-y-6">
          <Specimen name="Three across" note="the services and pillars row">
            <div className="grid gap-6 md:grid-cols-3">
              {[
                {
                  when: "8:00am",
                  title: "Morning Prayer",
                  note: "Prayer Book",
                  body: "The Prayer Book service, said quietly, finished by nine.",
                },
                {
                  when: "9:30am",
                  title: "Family service",
                  note: "all ages",
                  body: "Singing, a Bible talk, and kids' ministry running alongside.",
                },
                {
                  when: "5:00pm",
                  title: "Evening service",
                  note: "contemporary",
                  body: "A relaxed evening gathering, with a meal together afterwards.",
                },
              ].map((service) => (
                <Card key={service.title} className="flex h-full flex-col p-6">
                  <CardHeader className="p-0">
                    <Badge variant="accent" className="w-fit">
                      {service.when}
                    </Badge>
                    <CardTitle className="mt-4 font-heading text-2xl">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="capitalize">{service.note}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-1 flex-col p-0">
                    <p className="mt-4 flex-1 text-sm leading-relaxed text-muted">
                      {service.body}
                    </p>
                    <div className="mt-5">
                      <Button variant="outline" size="sm">
                        Read more
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </Specimen>

          <Specimen name="Two across" note="the bigger features, with the icon in the title">
            <div className="grid gap-6 md:grid-cols-2">
              {[
                {
                  icon: <Users className="h-5 w-5" />,
                  title: "Kids and youth",
                  body: "Age-grouped teaching on Sunday mornings and a Friday night youth group, all run by leaders with current checks.",
                },
                {
                  icon: <BookOpen className="h-5 w-5" />,
                  title: "Growth groups",
                  body: "Small groups through the week, in homes across the Hills, working through the same passage the Sunday talk covered.",
                },
              ].map((feature) => (
                <Card key={feature.title} className="flex h-full flex-col p-6">
                  <CardHeader className="p-0">
                    <CardTitle className="flex items-center gap-3 font-heading text-2xl">
                      <span
                        aria-hidden="true"
                        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-card bg-primary/10 text-primary"
                      >
                        {feature.icon}
                      </span>
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <p className="mt-4 text-sm leading-relaxed text-muted">{feature.body}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </Specimen>

          <Specimen name="Rows" note="icon, heading, description — for a plain list">
            <div className="space-y-4">
              {[
                {
                  icon: <Clock className="h-5 w-5" />,
                  title: "Three services each Sunday",
                  body: "8:00am, 9:30am and 5:00pm, each about an hour and a quarter.",
                },
                {
                  icon: <MapPin className="h-5 w-5" />,
                  title: "295 Windsor Road",
                  body: "On the corner of Cook Street, with parking on site and more on the street.",
                },
                {
                  icon: <CalendarDays className="h-5 w-5" />,
                  title: "Through the week",
                  body: "Growth groups, playgroup, youth and the men's and women's breakfasts.",
                },
              ].map((row) => (
                <div
                  key={row.title}
                  className="flex gap-4 rounded-card border border-border bg-paper p-6"
                >
                  <span
                    aria-hidden="true"
                    className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-card bg-primary/10 text-primary"
                  >
                    {row.icon}
                  </span>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-ink">{row.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted">{row.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </Specimen>
        </div>
      </Section>

      {/* ── i. Page furniture ──────────────────────────────────────────── */}
      <Section tone="paper" spacing="lg">
        <Heading
          id="furniture"
          title="Page furniture"
          blurb="The parts that top and tail a page: the heading band every inner page opens with, a coloured band to break up a long page, and the closing invitation to do something."
        />

        <div className="mt-8 space-y-6">
          <Specimen name="Page top" note="PageHeader — eyebrow, title, description, actions">
            <div className="overflow-hidden rounded-card border border-border">
              <PageHeader
                eyebrow="Our life together"
                title="Sunday services"
                description="Three services each Sunday, all built around the Bible read and taught."
                actions={<Button>Plan a visit</Button>}
              />
            </div>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted">
              Nine pages open this way. Most pass only the eyebrow, title and description —
              About is the one page that adds a button.
            </p>
          </Specimen>

          <Specimen name="Band" note="a strip of colour to break up a long page">
            <div className="overflow-hidden rounded-card border border-border">
              <Section tone="transparent" spacing="md" className="bg-band">
                <div className="mx-auto max-w-2xl text-center">
                  <h3 className="font-heading text-2xl font-extrabold text-ink">
                    Everyone is welcome
                  </h3>
                  <p className="mt-4 text-muted">
                    Come as you are. There is no dress code and no expectation that you know
                    what to do.
                  </p>
                </div>
              </Section>
            </div>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted">
              The band colour is optional: this theme has not set one, so it quietly borrows
              the card colour and the strip looks plain. Give{" "}
              <code className="font-mono text-xs">--color-band</code> a value in theme.css
              and every band on the site takes it at once. In the meantime pages alternate
              between the paper and card tones instead.
            </p>
          </Specimen>

          <Specimen name="Closing invitation" note="the two shapes used at the foot of a page">
            <div className="space-y-8">
              <div className="mx-auto max-w-2xl text-center">
                <p className="text-muted">
                  If you have children and are wondering whether Sunday morning will suit
                  them, the best answer is to come and see.
                </p>
                <div className="mt-6">
                  <Button>Plan a visit</Button>
                </div>
              </div>

              <div className="rounded-card border border-border bg-card p-6 sm:p-8">
                <h3 className="font-heading text-xl font-extrabold text-ink">
                  Looking for a group?
                </h3>
                <p className="mt-3 leading-relaxed text-muted">
                  Tell us roughly where you live and what your week looks like, and we will
                  point you to a group that fits.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Button size="lg">Find a group</Button>
                  <Button size="lg" variant="outline">
                    Ask a question
                  </Button>
                </div>
              </div>
            </div>
          </Specimen>
        </div>
      </Section>

      {/* ── j. Tables ──────────────────────────────────────────────────── */}
      <Section tone="card" spacing="lg">
        <Heading
          id="tables"
          title="Tables"
          blurb="How the office inbox lists things: a table inside a card, a search and a refresh above it, and the state of each row said in a word."
        />

        <div className="mt-8 space-y-6">
          <Specimen name="Data table" note="the admin inbox pattern">
            <div className="space-y-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Input
                  type="search"
                  className="sm:max-w-xs"
                  aria-label="Search messages"
                  placeholder="Search messages…"
                />
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted">3 shown</span>
                  <Button type="button" variant="outline" size="sm">
                    <RefreshCw className="h-4 w-4" aria-hidden="true" />
                    Refresh
                  </Button>
                </div>
              </div>

              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Received</TableHead>
                        <TableHead>From</TableHead>
                        <TableHead>Message</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[
                        {
                          day: "12 Mar 2026",
                          who: "Jane Smith",
                          email: "jane@example.com",
                          message: "Question about the 9:30am service",
                          status: <Badge variant="success">Replied</Badge>,
                        },
                        {
                          day: "11 Mar 2026",
                          who: "Peter Nguyen",
                          email: "peter@example.com",
                          message: "Booking the hall for a birthday",
                          status: <Badge variant="warning">Waiting on us</Badge>,
                        },
                        {
                          day: "9 Mar 2026",
                          who: "No name given",
                          email: "left on a QR card",
                          message: "Thanks for the welcome on Sunday",
                          status: <Badge variant="muted">Archived</Badge>,
                        },
                      ].map((row) => (
                        <TableRow key={row.message}>
                          <TableCell className="whitespace-nowrap text-muted">
                            {row.day}
                          </TableCell>
                          <TableCell>
                            <div className="font-medium text-ink">{row.who}</div>
                            <div className="max-w-[12rem] truncate text-xs text-muted">
                              {row.email}
                            </div>
                          </TableCell>
                          <TableCell className="max-w-sm">
                            <span className="block truncate font-medium text-ink">
                              {row.message}
                            </span>
                          </TableCell>
                          <TableCell>{row.status}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="outline" size="sm">
                              Open
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted">
              In the real inbox the whole row is clickable rather than carrying an Open
              button, and the status word sits in its own column exactly like this. Keep the
              date column from wrapping, let the long message truncate, and put any action
              at the right-hand end.
            </p>
          </Specimen>
        </div>
      </Section>

      {/* ── k. On-brand combinations ───────────────────────────────────── */}
      <Section tone="paper" spacing="lg">
        <Heading
          id="combinations"
          title="Put together"
          blurb="The same pieces arranged the way real pages use them — this is where you can tell whether a new palette actually works."
        />

        <div className="mt-8 space-y-8">
          {/* 1. Welcome band */}
          <div className="overflow-hidden rounded-card border border-border shadow-card">
            <Hero
              animated={false}
              eyebrow="This Sunday"
              title="You would be very welcome"
              description="Whatever you believe, whatever your week has held, there is a seat for you."
              actions={
                <>
                  {/* Mirrors the REAL hero on the site: the primary call to
                      action wears the secondary brand colour. If the site's
                      pattern changes, change it here too — a specimen that
                      disagrees with the live pages is worse than none. */}
                  <Button variant="secondary" size="lg">
                    Plan a visit
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    className="border-primary-fg/40 text-primary-fg"
                  >
                    What to expect
                  </Button>
                </>
              }
            />
          </div>

          {/* 2. Card row */}
          <div className="grid gap-5 md:grid-cols-3">
            {[
              {
                badge: "Weekly",
                title: "Morning Prayer",
                description: "8:00am",
                body: "The Prayer Book service, said quietly, finished by nine.",
              },
              {
                badge: "All ages",
                title: "Family service",
                description: "9:30am",
                body: "Singing, a Bible talk, and kids' ministry running alongside.",
              },
              {
                badge: "Evening",
                title: "5pm gathering",
                description: "5:00pm",
                body: "A relaxed evening service, with a meal together afterwards.",
              },
            ].map((item) => (
              <Card key={item.title} className="flex flex-col">
                <CardHeader>
                  <Badge variant="muted" className="w-fit">
                    {item.badge}
                  </Badge>
                  <CardTitle className="mt-2">{item.title}</CardTitle>
                  <CardDescription>{item.description}</CardDescription>
                </CardHeader>
                <CardContent className="flex-1">
                  <p className="text-sm leading-relaxed text-muted">{item.body}</p>
                </CardContent>
                <CardFooter>
                  <Button variant="link" className="px-0">
                    Read more
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          {/* 3. Call-to-action strip */}
          <div className="rounded-card bg-primary px-6 py-10 text-primary-fg shadow-card sm:px-10">
            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div className="max-w-xl">
                <p className="text-sm font-bold uppercase tracking-wide text-accent">
                  Get in touch
                </p>
                <h3 className="mt-2 font-heading text-2xl font-extrabold sm:text-3xl">
                  Questions before you come?
                </h3>
                <p className="mt-3 leading-relaxed text-primary-fg/85">
                  The office is happy to answer anything at all — no question is too small.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 md:shrink-0">
                <Button variant="accent" size="lg">
                  Contact the office
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="border-primary-fg/40 text-primary-fg"
                >
                  Find us
                </Button>
              </div>
            </div>
          </div>

          {/* 4. Footer-ish band */}
          <div className="rounded-card border border-border bg-card p-8 shadow-soft">
            <div className="grid gap-8 sm:grid-cols-3">
              <div>
                <span
                  aria-hidden="true"
                  className="flex h-10 w-10 items-center justify-center rounded bg-primary font-heading text-sm font-bold text-primary-fg"
                >
                  HT
                </span>
                <p className="mt-4 text-sm leading-relaxed text-muted">
                  Connect Sydney — Jesus | People | Campus.
                </p>
              </div>
              <div>
                <h4 className="font-heading text-sm font-bold uppercase tracking-wide text-ink">
                  Sundays
                </h4>
                <ul className="mt-3 space-y-2 text-sm text-muted">
                  <li>8:00am · Prayer Book</li>
                  <li>9:30am · Family service</li>
                  <li>5:00pm · Evening</li>
                </ul>
              </div>
              <div>
                <h4 className="font-heading text-sm font-bold uppercase tracking-wide text-ink">
                  Find us
                </h4>
                <ul className="mt-3 space-y-2 text-sm text-muted">
                  <li>Sydney, NSW</li>
                  <li>
                    <span className="font-semibold text-primary">Office hours</span> · Tue–Fri
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-6 text-xs text-muted">
              <span>© Connect Sydney</span>
              <Badge variant="outline">Design system preview</Badge>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}
